import type { FC } from "react";
import {
  ObservationBundle,
  ObservationBundleCategory
} from "src/types/observationLaboratory";
import {
  getBundleLabProviders,
  getBundleObservationSources,
  resolveObservationBundleCategory,
  valueMatches
} from "./resolveObservationBundleCategory";
import { LabLogoLabcorpIcon } from "src/ui/assets/icons/LabLogoLabcorpIcon";
import { LabLogoQuestIcon } from "src/ui/assets/icons/LabLogoQuestIcon";
import { LabLogoGetLabsIcon } from "src/ui/assets/icons/LabLogoGetLabsIcon";
import { LabLogoTassoIcon } from "src/ui/assets/icons/LabLogoTassoIcon";
import { LabLogoImportedIcon } from "src/ui/assets/icons/LabLogoImportedIcon";
import { LabLogoUploadedIcon } from "src/ui/assets/icons/LabLogoUploadedIcon";

// brand logo -> candidate tokens/domains. Matched via the shared valueMatches (host-aware).
const BRAND_LOGO_ENTRIES: Array<{ Icon: FC; values: string[] }> = [
  { Icon: LabLogoLabcorpIcon, values: ["LABCORP", "labcorp.com"] },
  {
    Icon: LabLogoQuestIcon,
    values: ["QUEST", "QUEST_DIAGNOSTICS", "questdiagnostics.com"]
  },
  { Icon: LabLogoGetLabsIcon, values: ["GETLABS", "getlabs.com"] },
  { Icon: LabLogoTassoIcon, values: ["TASSO", "tassoinc.com"] }
];

const brandFor = (values: string[]): FC | undefined =>
  values
    .map(
      (v) =>
        BRAND_LOGO_ENTRIES.find((entry) =>
          entry.values.some((c) => valueMatches(v, c))
        )?.Icon
    )
    .find(Boolean);

/** Resolves a bundle to its display logo. Never throws; defaults to Uploaded. */
export function resolveObservationBundleIcon(bundle: ObservationBundle): FC {
  if (!bundle) {
    return LabLogoUploadedIcon;
  }
  const brand =
    brandFor(getBundleObservationSources(bundle)) ?? // source outranks provider
    brandFor(getBundleLabProviders(bundle));
  if (brand) {
    return brand;
  }
  return resolveObservationBundleCategory(bundle) ===
    ObservationBundleCategory.IMPORTED
    ? LabLogoImportedIcon
    : LabLogoUploadedIcon;
}
